import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdAdd,
  MdCall,
  MdClose,
  MdNavigation,
  MdRemoveCircleOutline,
  MdWarehouse,
  MdZoomOutMap,
} from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import { agoText, metersText } from "./driverLocation";
import { navigateUrl } from "./routeSheet";
import {
  centreAbove,
  labelWidth,
  placeLabels,
  START,
  stopDetails,
  stopName,
  telHref,
} from "./routeMapStops";

// Where the map waits until the stops are placed (they're then fitted in).
const DEFAULT_CENTER = { lat: 45.508888, lng: -73.561668 };
// Room around the run when it's fitted in: the buttons sit at the top, the
// pins rise above their points and labels go beside them.
const FIT_PADDING = { top: 72, right: 56, bottom: 28, left: 56 };
// A stop picked in the list is shown at least this close (a town).
const FOCUS_ZOOM = 12;
// The open card: its size, how far above the stop's point its bottom sits,
// the margin it keeps from the map's edges, and from its top (clear of the
// buttons there).
const CARD = { width: 264, height: 300, gap: 47, edge: 10, top: 52 };
// How many item lines and earlier deliveries a card lists (the rest is
// counted), and photos per delivery.
const CARD_LINES = 5;
const CARD_DELIVERIES = 3;
const CARD_PHOTOS = 4;
// A candidate's key on the map (an order that could join the run).
const candidateKey = (id) => `add:${id}`;

// How far below the middle of a map `height` px tall a stop picked in the
// list is put: its pin and card together in the middle, the card clear of
// the buttons when the map is short, the pin still on it.
const cardRoom = (height) => {
  const middle = (CARD.gap + CARD.height) / 2;
  if (!height) return middle;
  return Math.min(
    Math.max(middle, CARD.gap + CARD.height + CARD.top - height / 2),
    height / 2 - CARD.edge,
  );
};
// Pins (a numbered circle on a point), as drawn below: half their width
// and their height above the point; the start's badge is centred on it.
const PIN = { half: 14, height: 33 };
const PICKED_PIN = { half: 17, height: 41 };
const BADGE = { half: 16 };
// Label heights: a stop's two lines, the start's one.
const LABEL_HEIGHT = { stop: 34, start: 22 };
// Stops at the same place stand side by side, this far apart.
const TWIN_SHIFT = 22;

export const MAP_BUTTON =
  "flex h-[32px] min-w-[32px] shrink-0 items-center justify-center gap-[5px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[8px] font-manrope text-[12px] font-semibold text-ui-text shadow-ui-sm transition-colors hover:border-brand hover:bg-brand-light hover:text-brand";
// w-max: the label hangs off a 26px pin, so without it the box shrinks to
// nothing (its lines truncate) and shows as an empty white capsule.
const LABEL_BOX =
  "pointer-events-auto absolute top-[0px] grid w-max max-w-[184px] gap-[1px] whitespace-nowrap rounded-[6px] border-[1px] border-ui-border bg-ui-surface px-[7px] py-[3px] text-left leading-[1.25] shadow-ui-sm";
const CARD_LINK =
  "flex items-center gap-[4px] font-semibold text-brand hover:underline";
const CARD_SECTION =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted";
const CARD_ACTION =
  "mt-[8px] flex w-full items-center justify-center gap-[5px] rounded-[7px] border-[1px] px-[10px] py-[6px] font-manrope text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";

const HISTORY_LABEL = {
  delivered: "Delivered",
  partly: "Partly delivered",
  not_delivered: "Not delivered",
};
const noteDay = (when) => {
  const date = when ? new Date(when) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";
};

// A card's "To deliver now" (each line still to deliver, how many) and
// "Delivered before" (the delivery list's notes, newest first, with the
// driver's photos).
const StopItems = ({ item }) => {
  const lines = item.toDeliver ?? [];
  const history = item.history ?? [];
  return (
    <>
      {lines.length > 0 ? (
        <div className="mt-[8px]">
          <span className={CARD_SECTION}>To deliver now</span>
          <ul className="m-[0px] mt-[2px] grid list-none gap-[1px] p-[0px]">
            {lines.slice(0, CARD_LINES).map((line, index) => (
              <li key={line.id ?? index} className="flex gap-[5px]">
                <b className="shrink-0 tabular-nums">{line.quantity} ×</b>
                <span className="min-w-[0px] truncate" title={line.name}>
                  {line.name}
                </span>
              </li>
            ))}
            {lines.length > CARD_LINES && (
              <li className="text-ui-text-muted">
                +{lines.length - CARD_LINES} more
              </li>
            )}
          </ul>
        </div>
      ) : (
        item.fulfilled && (
          <p className="m-[0px] mt-[8px] font-semibold text-state-success-text">
            Nothing left to deliver: fulfilled
          </p>
        )
      )}
      {history.length > 0 && (
        <div className="mt-[8px] border-t-[1px] border-ui-border-subtle pt-[6px]">
          <span className={CARD_SECTION}>
            {lines.length > 0 ? "Delivered before" : "Deliveries"}
          </span>
          {history.slice(0, CARD_DELIVERIES).map((entry, index) => (
            <div key={entry.id ?? index} className="mt-[4px] grid gap-[1px]">
              <span className="text-[11.5px]">
                <b>{HISTORY_LABEL[entry.kind]}</b>
                {noteDay(entry.when) && (
                  <span className="text-ui-text-muted">
                    {" "}
                    · {noteDay(entry.when)}
                  </span>
                )}
              </span>
              {entry.details.map((line) => (
                <span
                  key={line}
                  title={line}
                  className="truncate text-[11px] text-ui-text-muted"
                >
                  {line}
                </span>
              ))}
              {entry.photos.length > 0 && (
                <span className="mt-[2px] flex flex-wrap gap-[4px]">
                  {entry.photos.slice(0, CARD_PHOTOS).map((url, photo) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={url}
                        alt={`Delivery proof ${photo + 1}, ${HISTORY_LABEL[entry.kind].toLowerCase()}${noteDay(entry.when) ? ` ${noteDay(entry.when)}` : ""}`}
                        loading="lazy"
                        className="h-[36px] w-[36px] rounded-[4px] border-[1px] border-ui-border object-cover"
                      />
                    </a>
                  ))}
                  {entry.photos.length > CARD_PHOTOS && (
                    <span className="self-center text-[11px] text-ui-text-muted">
                      +{entry.photos.length - CARD_PHOTOS}
                    </span>
                  )}
                </span>
              )}
            </div>
          ))}
          {history.length > CARD_DELIVERIES && (
            <span className="mt-[2px] block text-[11px] text-ui-text-muted">
              {history.length - CARD_DELIVERIES} more in the order's notes
            </span>
          )}
        </div>
      )}
    </>
  );
};

const tokenColor = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// One OverlayView holding the markers (in the pane that takes clicks) and
// the open card (in the pane above it). React draws into both, again each
// time Google redraws the overlay (a zoom, a long pan).
const useOverlay = (maps, map) => {
  const [overlay, setOverlay] = useState(null);
  const [, setFrame] = useState(0);
  useEffect(() => {
    if (!map) return undefined;
    const view = new maps.OverlayView();
    const marks = document.createElement("div");
    const card = document.createElement("div");
    view.onAdd = () => {
      const panes = view.getPanes();
      panes.overlayMouseTarget.appendChild(marks);
      panes.floatPane.appendChild(card);
    };
    view.draw = () => setFrame((frame) => frame + 1);
    view.onRemove = () => {
      marks.remove();
      card.remove();
    };
    // A click on a marker isn't a click on the map (which closes the card),
    // and dragging or scrolling on the card doesn't move the map.
    maps.OverlayView.preventMapHitsFrom?.(marks);
    maps.OverlayView.preventMapHitsAndGesturesFrom?.(card);
    view.setMap(map);
    setOverlay({ view, marks, card });
    // A pan doesn't redraw the overlay: once the map stops, the labels are
    // placed again for what's on screen now.
    const idle = map.addListener("idle", () => setFrame((frame) => frame + 1));
    return () => {
      idle.remove();
      view.setMap(null);
    };
  }, [maps, map]);
  return overlay;
};

// Each marker's point in the overlay's pixels (stops at the same place set
// side by side) and the box it covers there.
const placeMarks = (projection, start, stops, selectedId, candidates = []) => {
  const marks = [];
  const twins = new Map();
  const add = (key, kind, item) => {
    const point = projection.fromLatLngToDivPixel(item.place);
    if (!point) return;
    const spot = `${item.place.lat.toFixed(5)},${item.place.lng.toFixed(5)}`;
    const nth = twins.get(spot) ?? 0;
    twins.set(spot, nth + 1);
    const x = point.x + nth * TWIN_SHIFT;
    const { y } = point;
    const pin = key === selectedId ? PICKED_PIN : PIN;
    const box =
      kind === "start"
        ? {
            left: x - BADGE.half,
            right: x + BADGE.half,
            top: y - BADGE.half,
            bottom: y + BADGE.half,
          }
        : {
            left: x - pin.half,
            right: x + pin.half,
            top: y - pin.height,
            bottom: y,
          };
    marks.push({ key, kind, item, x, y, box });
  };
  stops.forEach((stop) => stop.place && add(stop.id, "stop", stop));
  if (start?.place) add(START, "start", start);
  candidates.forEach(
    (order) => order.place && add(candidateKey(order.id), "candidate", order),
  );
  return marks;
};

// The map's own area in the overlay's pixels, so labels stay on it; null
// until it has a size.
const visibleArea = (projection, div, place) => {
  const seen = projection.fromLatLngToContainerPixel?.(place);
  const point = projection.fromLatLngToDivPixel(place);
  if (!seen || !point || !div?.clientWidth || !div.clientHeight) return null;
  const left = point.x - seen.x;
  const top = point.y - seen.y;
  return {
    left,
    top,
    right: left + div.clientWidth,
    bottom: top + div.clientHeight,
  };
};

// The badge on a card and in the list: the stop's number, or the start's
// warehouse.
export const StopBadge = ({ number = null, size = 26 }) => (
  <span
    className={`grid shrink-0 place-items-center font-manrope font-extrabold leading-none text-white ${
      number == null ? "rounded-[7px] bg-ui-inverted" : "rounded-full bg-brand"
    }`}
    style={{
      height: size,
      minWidth: size,
      fontSize: size > 26 ? 13 : 12,
      padding: "0 4px",
    }}
  >
    {number == null ? (
      <MdWarehouse aria-hidden="true" className="h-[15px] w-[15px]" />
    ) : (
      number
    )}
  </span>
);

// The card of the selected stop (or the start, or an order that could join
// the run), above its marker. A stop `removable` has Take off the run
// (`onRemove(id)`); a candidate has Add to the run (`onAdd(id)`).
const StopCard = ({
  mark,
  count,
  onClose,
  removable = false,
  onRemove,
  onAdd,
  busy = false,
}) => {
  const { item } = mark;
  const isStart = mark.kind === "start";
  const isCandidate = mark.kind === "candidate";
  return (
    <section
      aria-label={
        isStart
          ? `Start and end: ${item.name}`
          : isCandidate
            ? `Not on a run: ${stopName(item)}`
            : `Stop ${item.number}`
      }
      className="pointer-events-auto absolute max-h-[300px] w-[264px] max-w-[calc(100vw-40px)] -translate-x-1/2 -translate-y-full overflow-y-auto rounded-[10px] border-[1px] border-ui-border bg-ui-surface p-[12px] text-left font-manrope text-[12px] leading-[1.4] text-ui-text shadow-ui-md"
      style={{ left: mark.x, top: mark.y - CARD.gap }}
    >
      <div className="flex items-start gap-[9px]">
        {isCandidate ? (
          <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full border-[2px] border-dashed border-ui-border-strong bg-ui-surface text-ui-text-muted">
            <MdAdd aria-hidden="true" className="h-[16px] w-[16px]" />
          </span>
        ) : (
          <StopBadge number={isStart ? null : item.number} size={30} />
        )}
        <div className="grid min-w-[0px] flex-1 gap-[1px]">
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted">
            {isStart
              ? "Start and end"
              : isCandidate
                ? "Not on a run yet"
                : `Stop ${item.number} of ${count}`}
          </span>
          <span className="text-[14.5px] font-extrabold leading-[1.25] text-ui-text">
            {isStart ? item.name : stopName(item)}
          </span>
          {!isStart && (
            <span className="text-[11.5px] text-ui-text-muted">
              {stopDetails(item)}
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label="Close card"
          onClick={onClose}
          className="-mr-[4px] -mt-[4px] grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[6px] border-0 bg-[transparent] p-[0px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text"
        >
          <MdClose className="h-[16px] w-[16px]" />
        </button>
      </div>
      {!isStart && (
        <p className="m-[0px] mt-[8px] font-semibold text-ui-text">
          {item.customer || "No name"}
        </p>
      )}
      <p className="m-[0px] text-ui-text-muted">{item.address}</p>
      {!isStart && <StopItems item={item} />}
      {!isStart && (
        <div className="mt-[8px] flex flex-wrap items-center gap-x-[14px] gap-y-[4px]">
          <a
            href={navigateUrl(item.address)}
            target="_blank"
            rel="noopener noreferrer"
            className={CARD_LINK}
          >
            <MdNavigation className="h-[13px] w-[13px]" />
            Navigate
          </a>
          {item.phone && (
            <a href={telHref(item.phone)} className={CARD_LINK}>
              <MdCall className="h-[13px] w-[13px]" />
              {item.phone}
            </a>
          )}
        </div>
      )}
      {isCandidate && onAdd && (
        <button
          type="button"
          onClick={() => onAdd(item.id)}
          disabled={busy}
          className={`${CARD_ACTION} border-brand bg-brand text-white hover:bg-brand-hover`}
        >
          <MdAdd aria-hidden="true" className="h-[15px] w-[15px]" />
          Add to the run
        </button>
      )}
      {!isStart && !isCandidate && removable && onRemove && (
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          disabled={busy}
          className={`${CARD_ACTION} border-ui-border bg-ui-surface text-state-danger-text hover:border-state-danger-border hover:bg-state-danger-bg`}
        >
          <MdRemoveCircleOutline
            aria-hidden="true"
            className="h-[15px] w-[15px]"
          />
          Take off the run
        </button>
      )}
    </section>
  );
};

/**
 * The run on Google's interactive map (the Maps JavaScript API, `maps`):
 * the road route (`path`, [{ lat, lng }]), the start (`start`: { name,
 * address, place }) as a warehouse badge, and each stop with a `place`
 * (`stops`: routeMapStops' stops) as a pin with its number and, where
 * there's room, a label with its reference and customer. The selected stop
 * (`selection`: { id, how, seq }, START for the start) is highlighted, with
 * a card of its details (`count`: the sheet's stops, for "Stop 3 of 14").
 * Picked in a list (how "list") the map centres on it; picked on the map it
 * moves only as far as the card needs. `onSelect(id | null, "map")`
 * reports picks on the map; a click on the map itself closes the card. The
 * run is fitted in when its places change (`fitKey`), not when the stops
 * only change order. `status` "placing" covers the map while Google places
 * the stops, "updating" says the route is being worked out again. `tools`
 * go beside the Whole run button. On an open run, `candidates` (stops as
 * mapStops makes them, with a `place`) are orders that could join it: small
 * pins whose card has Add to the run (`onAdd(id)`); a stop in
 * `removableIds` has Take off the run (`onRemove(id)`); `busy` holds both
 * while a change is saving. `cardless`: the selected stop is marked but has
 * no card (the driver view shows it under the map). `focus` ({ place, seq })
 * moves the map there, close up, each time its seq changes.
 */
const StopsMap = ({
  maps,
  start = null,
  stops = [],
  count = stops.length,
  path = null,
  fitKey = "",
  selection = null,
  onSelect,
  gestureHandling = "cooperative",
  status = null,
  tools = null,
  className = "",
  drops = [],
  driver = null,
  candidates = [],
  onAdd,
  removableIds = [],
  onRemove,
  busy = false,
  cardless = false,
  focus = null,
}) => {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const overlay = useOverlay(maps, map);
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  // The map is made once for its element; the first fit moves it to the run.
  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = new maps.Map(divRef.current, {
        center: DEFAULT_CENTER,
        zoom: 8,
        gestureHandling,
        clickableIcons: false,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      });
    }
    const made = mapRef.current;
    setMap(made);
    const click = made.addListener("click", () => onSelectRef.current?.(null));
    return () => click.remove();
    // Made once; its gestures are set when it's made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps]);

  // The road route, under the markers.
  useEffect(() => {
    if (!map || !path?.length) return undefined;
    const color = tokenColor("--color-brand");
    const line = new maps.Polyline({
      map,
      path,
      clickable: false,
      strokeOpacity: 0.8,
      strokeWeight: 5,
      zIndex: 1,
      ...(color && { strokeColor: color }),
    });
    return () => line.setMap(null);
  }, [maps, map, path]);

  // A stop recorded far from its address: a dashed line from where it was
  // recorded to the address, so the gap shows.
  const farKey = drops
    .filter((drop) => drop.far && drop.address)
    .map((drop) => `${drop.id}:${drop.place.lat},${drop.place.lng}`)
    .join("|");
  const dropsRef = useRef(drops);
  dropsRef.current = drops;
  useEffect(() => {
    if (!map || !farKey) return undefined;
    const color = tokenColor("--color-danger-solid") || "#c62828";
    const lines = dropsRef.current
      .filter((drop) => drop.far && drop.address)
      .map(
        (drop) =>
          new maps.Polyline({
            map,
            path: [drop.place, drop.address],
            clickable: false,
            strokeOpacity: 0,
            zIndex: 2,
            icons: [
              {
                icon: {
                  path: "M 0,-1 0,1",
                  strokeOpacity: 1,
                  strokeColor: color,
                  scale: 2.5,
                },
                offset: "0",
                repeat: "9px",
              },
            ],
          }),
      );
    return () => lines.forEach((line) => line.setMap(null));
  }, [maps, map, farKey]);

  const placesRef = useRef([]);
  placesRef.current = [start?.place, ...stops.map((stop) => stop.place)].filter(
    Boolean,
  );
  const fit = useCallback(() => {
    const places = placesRef.current;
    if (!map || !places.length) return;
    const spots = new Set(places.map((place) => `${place.lat},${place.lng}`));
    if (spots.size === 1) {
      map.setCenter(places[0]);
      map.setZoom(15);
      return;
    }
    const bounds = new maps.LatLngBounds();
    places.forEach((place) => bounds.extend(place));
    map.fitBounds(bounds, FIT_PADDING);
  }, [maps, map]);
  useEffect(() => {
    fit();
  }, [fit, fitKey]);

  // A new pick moves the map (not one it had when it opened: full screen
  // opens on the whole run).
  const placeOf = (id) =>
    id === START
      ? start?.place
      : (stops.find((stop) => stop.id === id)?.place ??
        candidates.find((order) => candidateKey(order.id) === id)?.place ??
        null);
  const seenRef = useRef(selection?.seq ?? 0);
  useEffect(() => {
    const seq = selection?.seq ?? 0;
    if (!map || seq === seenRef.current) return;
    seenRef.current = seq;
    const place = selection && placeOf(selection.id);
    if (!place) return;
    const div = divRef.current;
    if (selection.how === "list") {
      const now = map.getZoom() ?? FOCUS_ZOOM;
      const zoom = Math.max(now, FOCUS_ZOOM);
      if (zoom !== now) map.setZoom(zoom);
      map.panTo(centreAbove(place, cardRoom(div?.clientHeight), zoom));
      return;
    }
    const point = overlayRef.current?.view
      .getProjection?.()
      ?.fromLatLngToContainerPixel?.(place);
    if (!point || !div?.clientWidth) return;
    const half = CARD.width / 2 + CARD.edge;
    const over = point.x + half - div.clientWidth;
    const dx = point.x < half ? point.x - half : over > 0 ? over : 0;
    const top = point.y - CARD.gap - CARD.height - CARD.top;
    const dy = top < 0 ? top : 0;
    if (dx || dy) map.panBy(dx, dy);
    // Only for a new pick; the places are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selection?.seq]);

  // Center on me: the map goes to `focus`, close up.
  const focusSeen = useRef(focus?.seq ?? null);
  useEffect(() => {
    if (!map || !focus?.place || focus.seq === focusSeen.current) return;
    focusSeen.current = focus.seq;
    if ((map.getZoom() ?? 0) < 15) map.setZoom(15);
    map.panTo(focus.place);
    // Only for a new focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focus?.seq]);

  const selectedId = selection?.id ?? null;
  const pick = (id, picked) => onSelect?.(picked ? null : id, "map");
  const projection = overlay?.view.getProjection?.();
  const marks = projection
    ? placeMarks(projection, start, stops, selectedId, candidates)
    : [];
  const view = marks.length
    ? visibleArea(projection, divRef.current, marks[0].item.place)
    : null;
  // Labels for the stops by number, then the start; the selected one has
  // its card instead.
  const sides = placeLabels(
    marks.map((mark) => ({
      key: mark.key,
      box: mark.box,
      height: LABEL_HEIGHT[mark.kind],
      width:
        mark.key === selectedId || mark.kind === "candidate"
          ? 0
          : labelWidth(
              mark.kind === "start"
                ? [mark.item.name]
                : [stopName(mark.item), mark.item.customer],
            ),
    })),
    view,
  );
  const cardMark = cardless
    ? null
    : marks.find((mark) => mark.key === selectedId);

  const label = (mark, lines) => {
    const side = sides.get(mark.key);
    if (!side) return null;
    return (
      <span
        className={`${LABEL_BOX} ${
          side === "right" ? "left-[calc(100%+5px)]" : "right-[calc(100%+5px)]"
        }`}
      >
        {lines.map((line, index) => (
          <span
            key={index}
            className={`truncate ${
              index
                ? "text-[10.5px] font-medium text-ui-text-muted"
                : "text-[11px] font-bold text-ui-text"
            }`}
          >
            {line}
          </span>
        ))}
      </span>
    );
  };

  const markers = marks.map((mark) => {
    const picked = mark.key === selectedId;
    if (mark.kind === "candidate") {
      const order = mark.item;
      return (
        <button
          key={mark.key}
          type="button"
          aria-label={`Not on a run: ${stopName(order)}${
            order.customer ? `, ${order.customer}` : ""
          }`}
          aria-pressed={picked}
          title="Not on a run yet: tap to add it"
          onClick={() => pick(mark.key, picked)}
          className={`absolute grid -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full border-[2px] border-dashed bg-ui-surface p-[0px] text-ui-text-muted shadow-ui-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
            picked
              ? "z-[3] h-[26px] w-[26px] border-state-warning-solid text-state-warning-text"
              : "z-[0] h-[20px] w-[20px] border-ui-border-strong hover:z-[2] hover:border-brand hover:text-brand"
          }`}
          style={{ left: mark.x, top: mark.y }}
        >
          <MdAdd aria-hidden="true" className="h-[13px] w-[13px]" />
        </button>
      );
    }
    if (mark.kind === "start") {
      return (
        <button
          key={START}
          type="button"
          aria-label={`Start and end: ${mark.item.name}`}
          aria-pressed={picked}
          onClick={() => pick(START, picked)}
          className={`absolute grid h-[32px] w-[32px] -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-[9px] border-[2px] border-white bg-ui-inverted p-[0px] text-white shadow-ui-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
            picked
              ? "z-[3] ring-2 ring-state-warning-solid"
              : "z-[1] hover:z-[2]"
          }`}
          style={{ left: mark.x, top: mark.y }}
        >
          <MdWarehouse aria-hidden="true" className="h-[17px] w-[17px]" />
          {label(mark, [mark.item.name])}
        </button>
      );
    }
    const stop = mark.item;
    const pinColor = picked
      ? "var(--color-warning-solid)"
      : "var(--color-brand)";
    return (
      <button
        key={stop.id}
        type="button"
        aria-label={`Stop ${stop.number}: ${stopName(stop)}${
          stop.customer ? `, ${stop.customer}` : ""
        }`}
        aria-pressed={picked}
        onClick={() => pick(stop.id, picked)}
        className={`group absolute block -translate-x-1/2 -translate-y-full cursor-pointer border-0 bg-transparent p-[0px] font-manrope focus:outline-none ${
          picked ? "z-[3]" : "z-[1] hover:z-[2] focus-visible:z-[2]"
        }`}
        style={{ left: mark.x, top: mark.y }}
      >
        <span
          className={`mx-auto grid place-items-center rounded-full border-[2px] border-white font-extrabold leading-none text-white shadow-ui-md group-focus-visible:ring-2 group-focus-visible:ring-brand ${
            picked
              ? "h-[34px] min-w-[34px] px-[6px] text-[15px]"
              : "h-[26px] min-w-[26px] px-[5px] text-[12px]"
          }`}
          style={{ background: pinColor }}
        >
          {stop.number}
        </span>
        <span
          className="mx-auto -mt-[2px] block h-[0px] w-[0px] border-x-[5px] border-t-[9px] border-solid border-x-transparent"
          style={{ borderTopColor: pinColor }}
        />
        {label(mark, [stopName(stop), stop.customer].filter(Boolean))}
      </button>
    );
  });

  // Where each recorded stop was saved (green, or red when far from the
  // address) and the driver's position (blue), over the map.
  const at = (place) => projection?.fromLatLngToDivPixel(place) ?? null;
  const extras = projection
    ? [
        ...drops.map((drop) => {
          const point = at(drop.place);
          if (!point) return null;
          const where =
            drop.distance == null
              ? "with its location"
              : `${metersText(drop.distance)} from the address`;
          return (
            <span
              key={`drop-${drop.id}`}
              role="img"
              aria-label={`Stop ${drop.number} was recorded here, ${where}`}
              title={`Stop ${drop.number} was recorded here, ${where}`}
              className={`pointer-events-auto absolute z-[1] grid h-[20px] min-w-[20px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[6px] border-[2px] border-white px-[3px] font-manrope text-[10px] font-extrabold leading-none text-white shadow-ui-md ${
                drop.far ? "bg-state-danger-solid" : "bg-state-success-solid"
              }`}
              style={{ left: point.x, top: point.y }}
            >
              {drop.number}
            </span>
          );
        }),
        driver?.place && at(driver.place) ? (
          <span
            key="driver"
            role="img"
            aria-label={
              driver.self
                ? "You are here"
                : `The driver, seen ${agoText(driver.seenAt)}`
            }
            className="pointer-events-auto absolute z-[4] -translate-x-1/2 -translate-y-1/2"
            style={{ left: at(driver.place).x, top: at(driver.place).y }}
          >
            <span className="absolute left-1/2 top-1/2 h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-brand opacity-30" />
            <span className="relative block h-[16px] w-[16px] rounded-full border-[3px] border-white bg-brand shadow-ui-md" />
            <span className="absolute left-[calc(100%+6px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[6px] border-[1px] border-ui-border bg-ui-surface px-[6px] py-[2px] font-manrope text-[11px] font-bold text-ui-text shadow-ui-sm">
              {driver.self ? "You" : `Driver · ${agoText(driver.seenAt)}`}
            </span>
          </span>
        ) : null,
      ].filter(Boolean)
    : [];

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Full size either way: Google can set its own position on it. */}
      <div ref={divRef} className="absolute inset-0 z-[0] h-full w-full" />
      {status !== "placing" && (
        <div className="absolute right-[10px] top-[10px] z-[2] flex items-center gap-[6px]">
          <button
            type="button"
            onClick={fit}
            aria-label="Show the whole run"
            title="Show the whole run"
            className={MAP_BUTTON}
          >
            <MdZoomOutMap className="h-[16px] w-[16px]" />
          </button>
          {tools}
        </div>
      )}
      {status === "updating" && (
        <span
          role="status"
          className="absolute left-[10px] top-[10px] z-[2] rounded-full border-[1px] border-ui-border bg-ui-surface px-[10px] py-[6px] font-manrope text-[11.5px] font-semibold text-ui-text-muted shadow-ui-sm"
        >
          Updating the route…
        </span>
      )}
      {status === "placing" && (
        <div
          role="status"
          className="absolute inset-0 z-[3] grid place-items-center bg-ui-surface"
        >
          <span className="flex items-center gap-[8px] font-manrope text-[12px] font-medium text-ui-text-muted">
            <CircularLoader size={18} />
            Placing the stops on the map…
          </span>
        </div>
      )}
      {overlay &&
        createPortal(
          <>
            {markers}
            {extras}
          </>,
          overlay.marks,
        )}
      {overlay &&
        cardMark &&
        createPortal(
          <StopCard
            mark={cardMark}
            count={count}
            onClose={() => onSelect?.(null, "map")}
            removable={removableIds.some(
              (id) => String(id) === String(cardMark.item.id),
            )}
            onRemove={onRemove}
            onAdd={onAdd}
            busy={busy}
          />,
          overlay.card,
        )}
    </div>
  );
};

export default StopsMap;
